import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, Clock, Coffee, Mic2, Users, 
  Lightbulb, Globe2, Building2, Sparkles, MessageSquare,
  CalendarPlus, Download, Loader2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AgendaPage = () => {
  const [cmsAgenda, setCmsAgenda] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Conference date: April 17th, 2026
  const conferenceDate = new Date('2026-04-17');

  // Fetch CMS agenda on mount
  useEffect(() => {
    const fetchAgenda = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/cms/agenda`);
        if (response.ok) {
          const data = await response.json();
          setCmsAgenda(data);
        }
      } catch (err) {
        console.error('Failed to fetch CMS agenda:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchAgenda();
  }, []);

  // Default hardcoded schedule
  const defaultSchedule = [
    {
      time: '08:00',
      endTime: '09:00',
      title: 'Registration & Welcome Coffee',
      type: 'networking',
      icon: Coffee,
      description: 'Check in, grab your badge, and connect with fellow attendees over breakfast.'
    },
    {
      time: '09:00',
      endTime: '09:30',
      title: 'Opening Keynote',
      type: 'keynote',
      icon: Mic2,
      speaker: 'Vernand Morency',
      role: 'CEO, Galleon Point Properties',
      description: 'Setting the vision for global affordable housing transformation through technology and impact investing.'
    },
    {
      time: '09:30',
      endTime: '10:30',
      title: 'The Future of Quantum AI in Real Estate',
      type: 'session',
      icon: Sparkles,
      speaker: 'Global InTech AS Team',
      description: 'Exploring how quantum computing and AI are revolutionizing property analysis and investment strategies.'
    },
    {
      time: '10:30',
      endTime: '11:00',
      title: 'Networking Break',
      type: 'networking',
      icon: Coffee,
      description: 'Connect with speakers and attendees in our networking lounge.'
    },
    {
      time: '11:00',
      endTime: '12:00',
      title: 'Panel: Athletes as Impact Investors',
      type: 'panel',
      icon: Users,
      description: 'How professional athletes are leveraging their platforms and resources to address the affordable housing crisis.',
      speakers: ['Former NFL Players', 'Housing Policy Experts', 'Investment Strategists']
    },
    {
      time: '12:00',
      endTime: '13:30',
      title: 'Lunch & Networking',
      type: 'networking',
      icon: Coffee,
      description: 'Gourmet lunch with themed discussion tables on key housing challenges.'
    },
    {
      time: '13:30',
      endTime: '14:30',
      title: 'NexusImpact.AI Platform Launch',
      type: 'keynote',
      icon: Globe2,
      description: 'Official unveiling of the quantum enabled social impact investment platform. Live demo and first access for attendees.'
    },
    {
      time: '14:30',
      endTime: '15:30',
      title: 'Workshop: Building Sustainable Communities',
      type: 'workshop',
      icon: Building2,
      description: 'Interactive session on designing carbon neutral affordable housing developments.',
      isWorkshop: true
    },
    {
      time: '15:30',
      endTime: '16:00',
      title: 'Afternoon Break',
      type: 'networking',
      icon: Coffee,
      description: 'Refreshments and networking.'
    },
    {
      time: '16:00',
      endTime: '17:00',
      title: 'Fireside Chat: Policy & Innovation',
      type: 'session',
      icon: MessageSquare,
      description: 'Discussion on regulatory frameworks supporting affordable housing innovation across G20 nations.'
    },
    {
      time: '17:00',
      endTime: '17:30',
      title: 'Closing Remarks & Call to Action',
      type: 'keynote',
      icon: Lightbulb,
      description: 'Summarizing key takeaways and outlining the path forward for attendees to make impact.'
    },
    {
      time: '17:30',
      endTime: '19:30',
      title: 'Evening Reception',
      type: 'networking',
      icon: Users,
      description: 'Cocktails, live music, and continued networking opportunities.'
    }
  ];

  // Merge CMS agenda with defaults - CMS takes priority if available
  const schedule = useMemo(() => {
    if (cmsAgenda.length > 0) {
      const iconMap = {
        'keynote': Mic2,
        'session': Sparkles,
        'panel': Users,
        'break': Coffee,
        'networking': Users,
        'workshop': Building2
      };
      return cmsAgenda.map(item => {
        // Handle both regular hyphen and en-dash in time range
        const timeParts = item.time.split(/\s*[-–]\s*/);
        return {
          time: timeParts[0] || item.time,
          endTime: timeParts[1] || timeParts[0] || item.time,
          title: item.title,
          type: item.session_type,
          icon: iconMap[item.session_type] || Sparkles,
          speaker: item.speakers?.join(', ') || null,
          description: item.description || '',
          speakers: item.panelists?.length > 0 ? item.panelists : undefined
        };
      });
    }
    return defaultSchedule;
  }, [cmsAgenda]);

  const generateICS = () => {
    const formatDateICS = (date, time) => {
      const [hours, minutes] = time.split(':');
      const d = new Date(date);
      d.setHours(parseInt(hours), parseInt(minutes), 0, 0);
      return d.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };

    const events = schedule
      .filter(item => item.type !== 'networking' || item.title.includes('Reception'))
      .map(item => {
        const startDate = formatDateICS(conferenceDate, item.time);
        const endDate = formatDateICS(conferenceDate, item.endTime);
        const speaker = item.speaker ? `\\nSpeaker: ${item.speaker}` : '';
        
        return `BEGIN:VEVENT
DTSTART:${startDate}
DTEND:${endDate}
SUMMARY:${item.title}
DESCRIPTION:${item.description.replace(/,/g, '\\,')}${speaker}
LOCATION:Tsai Auditorium (S010)\\, CGIS South\\, Harvard University
STATUS:CONFIRMED
END:VEVENT`;
      });

    const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Quantum Housing Conference//EN
CALSCALE:GREGORIAN
METHOD:PUBLISH
X-WR-CALNAME:Quantum Housing Conference 2026
X-WR-TIMEZONE:America/New_York
${events.join('\n')}
END:VCALENDAR`;

    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'quantum-housing-conference-2026.ics';
    link.click();
    URL.revokeObjectURL(link.href);
  };

  // Generate PDF/Print version of agenda
  const generatePDF = () => {
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Quantum Housing Conference 2026 - Agenda</title>
        <style>
          body { font-family: 'Segoe UI', Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; color: #1a1a2e; }
          h1 { color: #14213D; font-size: 28px; margin-bottom: 5px; }
          .subtitle { color: #C5A059; font-size: 14px; margin-bottom: 30px; }
          .date-location { background: #14213D; color: white; padding: 15px 20px; border-radius: 8px; margin-bottom: 30px; }
          .date-location span { color: #C5A059; }
          .session { border-left: 4px solid #C5A059; padding: 15px 20px; margin-bottom: 15px; background: #f8f8f8; border-radius: 0 8px 8px 0; }
          .session.keynote { border-left-color: #C5A059; background: #FFF8E7; }
          .session.panel { border-left-color: #14213D; }
          .session.networking { border-left-color: #888; background: #f0f0f0; }
          .time { font-weight: bold; color: #14213D; font-size: 14px; }
          .title { font-size: 16px; font-weight: 600; margin: 5px 0; color: #14213D; }
          .description { font-size: 13px; color: #555; margin-top: 5px; }
          .speaker { font-size: 12px; color: #C5A059; margin-top: 5px; font-style: italic; }
          .type-badge { display: inline-block; font-size: 10px; padding: 2px 8px; border-radius: 10px; background: #14213D; color: white; margin-left: 10px; }
          .footer { margin-top: 40px; padding-top: 20px; border-top: 2px solid #C5A059; text-align: center; font-size: 12px; color: #666; }
          .footer a { color: #C5A059; }
          @media print { body { padding: 20px; } .session { break-inside: avoid; } }
        </style>
      </head>
      <body>
        <h1>Quantum Housing Conference 2026</h1>
        <p class="subtitle">How Athletes' Taxes Can Solve the Global Housing Crisis</p>
        <div class="date-location">
          <strong>Date:</strong> <span>Friday, April 17th, 2026</span> &nbsp;|&nbsp;
          <strong>Venue:</strong> <span>Tsai Auditorium, Harvard University</span>
        </div>
        
        ${schedule.map(item => `
          <div class="session ${item.type}">
            <div class="time">${item.time} – ${item.endTime} <span class="type-badge">${item.type}</span></div>
            <div class="title">${item.title}</div>
            ${item.description ? `<div class="description">${item.description}</div>` : ''}
            ${item.speaker ? `<div class="speaker">Speaker: ${item.speaker}</div>` : ''}
          </div>
        `).join('')}
        
        <div class="footer">
          <p><strong>Organized by:</strong> Global InTech AS | <a href="https://globalintech.ai">globalintech.ai</a></p>
          <p>Register at <a href="https://quantumhousing.ai/signup">quantumhousing.ai/signup</a></p>
        </div>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'keynote':
        return 'border-l-primary bg-primary/5';
      case 'session':
        return 'border-l-secondary bg-secondary/5';
      case 'panel':
        return 'border-l-accent bg-accent/5';
      case 'workshop':
        return 'border-l-green-500 bg-green-500/5';
      case 'networking':
        return 'border-l-muted-foreground bg-muted/30';
      default:
        return 'border-l-border';
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-12">
      {/* Hero */}
      <section className="px-6 md:px-12 lg:px-24 mb-16">
        <div className="max-w-4xl mx-auto text-center">
          <span className="text-sm uppercase tracking-widest text-secondary font-medium mb-4 block">
            April 17th, 2026 • Tsai Auditorium, Harvard University
          </span>
          <h1 className="font-outfit text-4xl md:text-6xl font-bold tracking-tight mb-6" data-testid="agenda-title">
            Conference <span className="text-gradient-quantum">Agenda</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
            A full day of inspiring keynotes, interactive workshops, and networking opportunities 
            designed to accelerate affordable housing innovation.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/signup"
              className="inline-flex items-center gap-2 px-8 py-4 bg-primary text-primary-foreground rounded-full font-semibold transition-all hover:-translate-y-1 glow-primary"
              data-testid="agenda-register-btn"
            >
              Register Now
              <ArrowRight className="w-5 h-5" />
            </Link>
            <button
              onClick={generateICS}
              className="inline-flex items-center gap-2 px-6 py-4 glass-card rounded-full font-semibold transition-all hover:bg-white/10"
              data-testid="add-to-calendar-btn"
            >
              <CalendarPlus className="w-5 h-5" />
              Add to Calendar
            </button>
            <button
              onClick={generatePDF}
              className="inline-flex items-center gap-2 px-6 py-4 glass-card rounded-full font-semibold transition-all hover:bg-white/10"
              data-testid="download-agenda-pdf-btn"
            >
              <Download className="w-5 h-5" />
              Download PDF
            </button>
          </div>
        </div>
      </section>

      {/* Legend */}
      <section className="px-6 md:px-12 lg:px-24 mb-12">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-wrap justify-center gap-4">
            <div className="flex items-center gap-2 text-sm">
              <div className="w-3 h-3 rounded-full bg-primary" />
              <span>Keynote</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <div className="w-3 h-3 rounded-full bg-secondary" />
              <span>Session</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <div className="w-3 h-3 rounded-full bg-accent" />
              <span>Panel</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <div className="w-3 h-3 rounded-full bg-green-500" />
              <span>Workshop</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <div className="w-3 h-3 rounded-full bg-muted-foreground" />
              <span>Networking</span>
            </div>
          </div>
        </div>
      </section>

      {/* Schedule */}
      <section className="px-6 md:px-12 lg:px-24 mb-20" data-testid="schedule-section">
        <div className="max-w-4xl mx-auto">
          <div className="space-y-4">
            {schedule.map((item, index) => (
              <div
                key={index}
                className={`glass-card rounded-xl p-6 border-l-4 ${getTypeColor(item.type)} transition-all hover:-translate-y-1`}
                data-testid={`agenda-item-${index}`}
              >
                <div className="flex flex-col md:flex-row md:items-start gap-4">
                  <div className="flex items-center gap-3 md:w-40 flex-shrink-0">
                    <Clock className="w-5 h-5 text-muted-foreground" />
                    <span className="font-mono text-lg font-semibold">
                      {item.time} – {item.endTime}
                    </span>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-background/50 flex items-center justify-center flex-shrink-0">
                        <item.icon className="w-5 h-5 text-primary" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-outfit text-xl font-semibold mb-1">
                          {item.title}
                        </h3>
                        {item.speaker && (
                          <p className="text-primary font-medium text-sm mb-2">
                            {item.speaker} {item.role && `• ${item.role}`}
                          </p>
                        )}
                        {item.speakers && (
                          <p className="text-muted-foreground text-sm mb-2">
                            Featuring: {item.speakers.join(', ')}
                          </p>
                        )}
                        <p className="text-muted-foreground">
                          {item.description}
                        </p>
                        {item.isWorkshop && (
                          <span className="inline-block mt-2 px-3 py-1 text-xs font-medium bg-green-500/20 text-green-400 rounded-full">
                            Limited Capacity – Register Early
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto text-center glass-card rounded-3xl p-12">
          <h2 className="font-outfit text-3xl font-bold mb-4">
            Secure Your Spot
          </h2>
          <p className="text-muted-foreground mb-8">
            Early bird registration closes soon. Don't miss this opportunity to be part of the change.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/signup"
              className="inline-flex items-center gap-2 px-10 py-5 bg-primary text-primary-foreground rounded-full font-semibold text-lg transition-all hover:-translate-y-1"
            >
              Register Now
              <ArrowRight className="w-5 h-5" />
            </Link>
            <button
              onClick={generateICS}
              className="inline-flex items-center gap-2 px-8 py-5 glass-card rounded-full font-semibold transition-all hover:bg-white/10"
              data-testid="cta-add-to-calendar-btn"
            >
              <Download className="w-5 h-5" />
              Download Schedule (.ics)
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AgendaPage;
